import { useCallback, useEffect, useState } from 'react'

import { API_BASE } from '../../config'
import { formatDate } from '../../lib/datetime'
import { initWalkrunLiff } from '../../lib/walkrunLiff'
import type { WalkrunSession } from '../../lib/walkrunLiff'

/* ============================================================================
   Dashboard Walk & Run — พนักงานเปิดจากแบนเนอร์บน rich menu

   url: https://simat2026.simat.work/dashboard/walkrun  (Endpoint ของ LIFF_ID_WALHRUN)

   ⚠️ อยู่โฮสต์เดียวกับหลังบ้าน แต่**คนละระบบยืนยันตัวตน** — ใช้ idToken ของ LINE
      ไม่ใช่ cookie session ของ admin → route นี้ต้องอยู่ **นอก** RequireAuth/Shell
      (ถ้าเผลอเอาไปไว้ข้างใน พนักงานจะถูกเด้งไปหน้า login ของผู้ดูแล)

   ⚠️ ยังไม่มีส่วน "ของฉัน" เพราะ CSV ที่ import มีแต่ชื่อที่ตั้งเองในแอปนับก้าว
      ไม่มีรหัสพนักงาน จับคู่กับคนที่เปิดดูไม่ได้ (ดูหัวไฟล์ 007_walkrun.sql)
      → หน้านี้แสดง "อันดับรวมของบริษัท" ซึ่งไม่ผูกกับตัวบุคคล
   ============================================================================ */

type Coverage = { firstDay: string | null; lastDay: string | null; people: number; dayRows: number }
type Row = { nameKey: string; name: string; steps: number; days: number }
type Data = {
  coverage: Coverage
  leaderboard: Row[]
  total: number
  totalSteps: number
  page: number
  pageSize: number
  me: { empCode: string; empName: string | null }
}

/** ก้าว → กม. คร่าว ๆ (จากไฟล์จริง: 20250 ก้าว = 14.81 กม.) */
const KM_PER_STEP = 0.000731
const nf = new Intl.NumberFormat('th-TH')
/** หน้าละ 10 แถวตามกติกาของทั้งระบบ (backend/src/lib/paging.js) */
const PAGE_SIZE = 10

export default function WalkrunDashboard() {
  const [step, setStep] = useState('กำลังเริ่ม…')
  const [session, setSession] = useState<WalkrunSession | null>(null)
  const [data, setData] = useState<Data | null>(null)
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // เข้า LIFF ครั้งเดียวตอนเปิดหน้า
  useEffect(() => {
    let alive = true
    initWalkrunLiff((s) => alive && setStep(s))
      .then((s) => alive && setSession(s))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      alive = false
    }
  }, [])

  const load = useCallback(
    async (idToken: string, wanted: number) => {
      setBusy(true)
      try {
        const qs = new URLSearchParams({ page: String(wanted), pageSize: String(PAGE_SIZE) })
        const res = await fetch(`${API_BASE}/api/walkrun/dashboard?${qs}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
        const body = (await res.json().catch(() => ({}))) as Partial<Data> & { error?: string }
        if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`)
        setData(body as Data)
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (session) void load(session.idToken, page)
  }, [session, page, load])

  if (error) {
    return (
      <main className="wr">
        <h1>Walk &amp; Run</h1>
        <p className="wr-error">{error}</p>
        <button type="button" onClick={() => window.location.reload()}>
          ลองใหม่
        </button>
      </main>
    )
  }

  if (!session || !data) {
    return (
      <main className="wr">
        <h1>Walk &amp; Run</h1>
        <p className="wr-meta">{session ? 'กำลังโหลดอันดับ…' : step}</p>
      </main>
    )
  }

  const { coverage, leaderboard, total, totalSteps } = data
  const pages = Math.max(Math.ceil(total / data.pageSize), 1)
  const rankOffset = (data.page - 1) * data.pageSize

  return (
    <main className="wr">
      <header className="wr-head">
        <p className="wr-kicker">โครงการสุขภาพ</p>
        <h1>Walk &amp; Run</h1>
        <p className="wr-meta">
          สวัสดี {data.me.empName ?? data.me.empCode} · อันดับรวมของทั้งบริษัท
        </p>
      </header>

      {coverage.dayRows === 0 ? (
        <p className="wr-empty">ยังไม่มีข้อมูล — รอฝ่ายบุคคลนำเข้าไฟล์ก้าวรอบแรก</p>
      ) : (
        <>
          <section className="wr-stats">
            <div>
              <b>{nf.format(total)}</b>
              <span>คนที่ร่วม</span>
            </div>
            <div>
              <b>{nf.format(totalSteps)}</b>
              <span>ก้าวรวม</span>
            </div>
            <div>
              <b>{(totalSteps * KM_PER_STEP).toFixed(1)}</b>
              <span>กม. รวม</span>
            </div>
          </section>

          <p className="wr-range">
            ข้อมูล {formatDate(coverage.firstDay)} – {formatDate(coverage.lastDay)}
          </p>

          <ol className="wr-board" start={rankOffset + 1}>
            {leaderboard.map((r, i) => {
              const rank = rankOffset + i + 1
              return (
                <li key={r.nameKey} data-top={rank <= 3 ? rank : undefined}>
                  <span className="wr-rank">{rank}</span>
                  <span className="wr-name">{r.name}</span>
                  <span className="wr-steps">
                    {nf.format(r.steps)}
                    <small>{(r.steps * KM_PER_STEP).toFixed(2)} กม.</small>
                  </span>
                </li>
              )
            })}
          </ol>

          {pages > 1 && (
            <nav className="wr-pager">
              <button type="button" disabled={busy || data.page <= 1} onClick={() => setPage(data.page - 1)}>
                ← ก่อนหน้า
              </button>
              <span>
                {data.page} / {pages}
              </span>
              <button
                type="button"
                disabled={busy || data.page >= pages}
                onClick={() => setPage(data.page + 1)}
              >
                ถัดไป →
              </button>
            </nav>
          )}
        </>
      )}

      <p className="wr-foot">
        อันดับคิดจากชื่อที่ตั้งไว้ในแอปนับก้าว — ถ้าไม่เจอชื่อตัวเอง แจ้งฝ่ายบุคคลให้ตรวจว่าชื่อในไฟล์ตรงกันไหม
      </p>
    </main>
  )
}
